import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
function LogoFull() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 32, height: 32, background: '#004D35', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 40 40" fill="none">
          <path d="M10 11 L20 28 L30 11" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="20" cy="28" r="2.5" fill="#a8f0c6" />
        </svg>
      </div>
      <span style={{ fontSize: 17, fontWeight: 700, color: '#004D35', fontFamily: 'Georgia, serif' }}>Viysr</span>
    </div>
  );
}

const G = '#004D35';
const MINT = '#a8f0c6';
const BG = '#f5f3ef';
const BORDER = '#e8e5e0';
const DARK = '#0a1a0f';
const RED = '#c0392b';
const RED_BG = '#fdf2f2';
const RED_BORDER = '#f5c6c2';
const AMBER = '#b45309';
const AMBER_BG = '#fffbeb';
const AMBER_BORDER = '#fcd34d';

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: DARK, color: '#fff', padding: '12px 28px', borderRadius: 999, fontSize: 14, fontFamily: 'sans-serif', zIndex: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
      {msg}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active:    { label: 'Active',    color: G,     bg: MINT },
    past_due:  { label: 'Past due',  color: AMBER,  bg: AMBER_BG },
    cancelled: { label: 'Cancelled', color: '#555', bg: '#e0ddd8' },
    paused:    { label: 'Paused',    color: '#7a5a00', bg: '#ffd97d' },
    none:      { label: 'Inactive',  color: '#aaa', bg: BG },
  };
  const s = map[status] || map.none;
  return (
    <span style={{ fontSize: 11, fontFamily: 'sans-serif', fontWeight: 700, color: s.color, background: s.bg, padding: '3px 10px', borderRadius: 999, display: 'inline-block', border: `1px solid ${s.bg}` }}>
      {s.label}
    </span>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(10,26,15,0.5)', backdropFilter: 'blur(6px)' }} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 20, width: '100%', maxWidth: 460, boxShadow: '0 32px 80px rgba(0,0,0,0.2)', zIndex: 1, padding: '32px 32px 28px' }}>
        {children}
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: `1.5px solid ${BORDER}`, borderRadius: 16, padding: '28px 32px', marginBottom: 20 }}>
      {(title || subtitle) && (
        <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${BORDER}` }}>
          {title && <h2 style={{ fontSize: 17, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 4px' }}>{title}</h2>}
          {subtitle && <p style={{ fontSize: 13, color: '#aaa', fontFamily: 'sans-serif', margin: 0 }}>{subtitle}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── UPDATE PAYMENT FORM ─────────────────────────────────────────────────────
const CARD_STYLE = {
  style: {
    base: { fontSize: '15px', fontFamily: 'system-ui, sans-serif', color: DARK, '::placeholder': { color: '#bbb' } },
    invalid: { color: RED },
  },
};

function UpdatePaymentForm({ getAuthHeader, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!stripe || !elements || !cardComplete) return;
    setSaving(true);
    setError(null);
    try {
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });
      if (pmError) { setError(pmError.message); setSaving(false); return; }

      const res = await fetch('/api/billing/update-payment', {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethodId: paymentMethod.id }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to update.'); setSaving(false); return; }
      onSuccess();
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ border: `1.5px solid ${BORDER}`, borderRadius: 10, padding: '14px 16px', background: '#fff', marginBottom: 12 }}>
        <CardElement options={CARD_STYLE} onChange={e => setCardComplete(e.complete)} />
      </div>
      {error && <div style={{ fontSize: 13, color: RED, marginBottom: 12, fontFamily: 'sans-serif' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={handleSave}
          disabled={!cardComplete || saving}
          style={{ background: cardComplete ? G : '#bbb', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: cardComplete ? 'pointer' : 'default', fontFamily: 'sans-serif' }}
        >
          {saving ? 'Saving…' : 'Save new card'}
        </button>
        <button onClick={onCancel} style={{ background: '#fff', color: '#888', border: `1.5px solid ${BORDER}`, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'sans-serif' }}>Cancel</button>
      </div>
    </div>
  );
}

// ─── SETUP STRIPE FORM (for beta advisors adding first card) ─────────────────
function SetupStripeForm({ getAuthHeader, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (!stripe || !elements || !cardComplete) return;
    setSaving(true);
    setError(null);
    try {
      const { paymentMethod, error: pmError } = await stripe.createPaymentMethod({
        type: 'card',
        card: elements.getElement(CardElement),
      });
      if (pmError) { setError(pmError.message); setSaving(false); return; }

      const res = await fetch('/api/billing/setup-stripe', {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethodId: paymentMethod.id }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed to set up billing.'); setSaving(false); return; }
      onSuccess();
    } catch (e) {
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, color: '#555', fontFamily: 'sans-serif', marginBottom: 12, lineHeight: 1.5 }}>
        Add a payment method to keep your profile active after the beta period ends.
      </div>
      <div style={{ border: `1.5px solid ${BORDER}`, borderRadius: 10, padding: '14px 16px', background: '#fff', marginBottom: 12 }}>
        <CardElement options={CARD_STYLE} onChange={e => setCardComplete(e.complete)} />
      </div>
      {error && <div style={{ fontSize: 13, color: RED, marginBottom: 12, fontFamily: 'sans-serif' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={handleSave}
          disabled={!cardComplete || saving}
          style={{ background: cardComplete ? G : '#bbb', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: cardComplete ? 'pointer' : 'default', fontFamily: 'sans-serif' }}
        >
          {saving ? 'Saving…' : 'Add payment method'}
        </button>
        {onCancel && <button onClick={onCancel} style={{ background: '#fff', color: '#888', border: `1.5px solid ${BORDER}`, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'sans-serif' }}>Cancel</button>}
      </div>
    </div>
  );
}

// ─── CANCEL MODAL ─────────────────────────────────────────────────────────────
function CancelModal({ periodEnd, onConfirm, onClose, cancelling }) {
  return (
    <Modal onClose={onClose}>
      <div style={{ width: 48, height: 48, background: RED_BG, border: `1.5px solid ${RED_BORDER}`, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, marginBottom: 20 }}>✕</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', margin: '0 0 10px' }}>Cancel subscription?</h2>
      <p style={{ fontSize: 14, fontFamily: 'sans-serif', color: '#666', lineHeight: 1.65, margin: '0 0 16px' }}>
        You can cancel anytime — no contract, no penalty.
      </p>
      <div style={{ background: BG, border: `1.5px solid ${BORDER}`, borderRadius: 12, padding: '14px 16px', marginBottom: 24 }}>
        <p style={{ fontSize: 13, fontFamily: 'sans-serif', color: '#555', margin: 0, lineHeight: 1.6 }}>
          Your listing will stay <strong>active and visible</strong>{periodEnd ? ` until ${periodEnd}` : ''} — the end of your current billing period. After that, your profile will be removed from Viysr.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={onClose} style={{ flex: 1, background: '#fff', color: '#888', border: `1.5px solid ${BORDER}`, padding: '12px', borderRadius: 999, fontSize: 14, fontFamily: 'sans-serif', fontWeight: 600, cursor: 'pointer' }}>Keep subscription</button>
        <button onClick={onConfirm} disabled={cancelling} style={{ flex: 1, background: cancelling ? '#bbb' : RED, color: '#fff', border: 'none', padding: '12px', borderRadius: 999, fontSize: 14, fontFamily: 'sans-serif', fontWeight: 700, cursor: cancelling ? 'default' : 'pointer' }}>
          {cancelling ? 'Cancelling…' : 'Cancel anyway'}
        </button>
      </div>
    </Modal>
  );
}

// ─── TOGGLE ───────────────────────────────────────────────────────────────────
function Toggle({ on, onChange }) {
  return (
    <button
      onClick={() => onChange(!on)}
      style={{ width: 40, height: 22, borderRadius: 999, border: 'none', background: on ? G : '#ddd', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
    >
      <span style={{ position: 'absolute', top: 2, left: on ? 20 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
    </button>
  );
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const AdvisorBillingSettings = () => {
  const { user, getAuthHeader, signOut } = useAuth();
  const navigate = useNavigate();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpdatePayment, setShowUpdatePayment] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [configMonthlyPrice, setConfigMonthlyPrice] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  // Load Stripe publishable key and billing details
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, detailsRes] = await Promise.all([
        fetch('/api/billing/config'),
        fetch('/api/billing/details', { credentials: 'include', headers: getAuthHeader() }),
      ]);

      if (configRes.ok) {
        const cfg = await configRes.json();
        if (cfg.publishableKey) setStripePromise(loadStripe(cfg.publishableKey));
        if (cfg.livePrice?.amount != null) setConfigMonthlyPrice(cfg.livePrice.amount / 100);
      }

      if (detailsRes.ok) {
        const data = await detailsRes.json();
        setDetails(data);
      } else {
        setError('Failed to load billing information.');
      }
    } catch (e) {
      console.error('[Billing] Load error:', e);
      setError('Failed to load billing information.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'advisor' && user.role !== 'admin') {
      navigate('/');
      return;
    }
    loadData();
  }, [user, navigate, loadData]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await fetch('/api/billing/cancel', {
        method: 'POST', credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (res.ok) {
        setShowCancelModal(false);
        showToast('Subscription cancelled — your listing stays active until the end of your billing period.');
        await loadData();
      } else {
        showToast(data.error || 'Failed to cancel. Please try again.');
      }
    } catch (e) {
      showToast('Something went wrong. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      const res = await fetch('/api/billing/reactivate', {
        method: 'POST', credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        showToast('Subscription reactivated! Your listing is visible again.');
        await loadData();
      } else {
        const data = await res.json();
        showToast(data.error || 'Failed to reactivate. Please try again.');
      }
    } catch (e) {
      showToast('Something went wrong. Please try again.');
    } finally {
      setReactivating(false);
    }
  };

  const handlePaymentUpdated = async () => {
    setShowUpdatePayment(false);
    showToast('Payment method updated successfully.');
    await loadData();
  };

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const res = await fetch('/api/billing/portal', {
        method: 'POST',
        credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok) { showToast(data.error || 'Could not open billing portal'); return; }
      window.open(data.url, '_blank', 'noopener');
    } catch (e) {
      showToast('Something went wrong. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handlePrefChange = async (key, val) => {
    setDetails(prev => ({ ...prev, [key]: val }));
    try {
      await fetch('/api/billing/preferences', {
        method: 'PATCH', credentials: 'include',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: val }),
      });
    } catch (e) {
      console.error('[Billing] Pref update error:', e);
    }
  };

  const handleCopyReferral = () => {
    if (!details?.referral_code) return;
    navigator.clipboard.writeText(details.referral_code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSignOut = async () => { await signOut(); navigate('/'); };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${BORDER}`, borderTopColor: G, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const billingStatus = details?.billing_status || 'none';
  const subscription = details?.subscription;
  const paymentMethod = details?.paymentMethod;
  const hasStripeCustomer = details?.hasStripeCustomer ?? true;
  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : null;
  const nextBillingDate = details?.next_billing_date
    ? new Date(details.next_billing_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : periodEnd;
  const isBeta = details?.isBeta;
  // Price comes from /api/billing/config (live Stripe lookup) — not from details response
  const monthlyPrice = configMonthlyPrice;
  const planType = details?.plan_type || 'beta';
  const compedUntil = details?.comped_until ? new Date(details.comped_until) : null;
  const isCompedAdvisor = planType === 'comped';
  const compExpired = isCompedAdvisor && compedUntil && compedUntil <= new Date();
  const compActive = isCompedAdvisor && compedUntil && compedUntil > new Date();
  const compedUntilLabel = compedUntil
    ? compedUntil.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;
  // True beta advisor = active, no Stripe, not comped
  const isBetaAdvisor = billingStatus === 'active' && !hasStripeCustomer && !isCompedAdvisor;

  return (
    <div style={{ background: BG, minHeight: '100vh', paddingBottom: 80 }}>
      <Toast msg={toast} />

      {showCancelModal && (
        <CancelModal
          periodEnd={periodEnd}
          onConfirm={handleCancel}
          onClose={() => setShowCancelModal(false)}
          cancelling={cancelling}
        />
      )}

      <nav style={{ background: '#fff', padding: '0 40px', borderBottom: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Link to="/advisor/dashboard" style={{ textDecoration: 'none' }}>
            <LogoFull size={26} />
          </Link>
          <span style={{ fontSize: 13, fontFamily: 'sans-serif', color: '#ccc' }}>→</span>
          <Link to="/advisor/dashboard" style={{ fontSize: 13, fontFamily: 'sans-serif', color: '#aaa', textDecoration: 'none' }}>Dashboard</Link>
          <span style={{ fontSize: 13, fontFamily: 'sans-serif', color: '#ccc' }}>→</span>
          <span style={{ fontSize: 13, fontFamily: 'sans-serif', color: '#444', fontWeight: 600 }}>Billing</span>
        </div>
        <button onClick={handleSignOut} style={{ background: 'none', border: 'none', fontSize: 13, fontFamily: 'sans-serif', color: '#bbb', cursor: 'pointer' }}>Sign out</button>
      </nav>

      <div style={{ maxWidth: 660, margin: '40px auto', padding: '0 24px' }}>

        {error && (
          <div style={{ background: RED_BG, border: `1.5px solid ${RED_BORDER}`, borderRadius: 12, padding: '14px 18px', marginBottom: 20, fontSize: 14, color: RED, fontFamily: 'sans-serif' }}>
            {error}
          </div>
        )}

        {/* Status banners */}
        {billingStatus === 'cancelled' && (
          <div style={{ background: '#fafaf8', border: `1.5px solid ${BORDER}`, borderRadius: 14, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'sans-serif', color: '#555', marginBottom: 2 }}>Subscription cancelled</div>
              <div style={{ fontSize: 13, fontFamily: 'sans-serif', color: '#aaa' }}>{periodEnd ? `Your listing stays active until ${periodEnd}.` : 'Your listing will be hidden at the end of your billing period.'}</div>
            </div>
            <button onClick={handleReactivate} disabled={reactivating} style={{ background: G, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontFamily: 'sans-serif', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {reactivating ? 'Reactivating…' : 'Reactivate →'}
            </button>
          </div>
        )}

        {/* ── SUBSCRIPTION ── */}
        <SectionCard title="Subscription" subtitle={isCompedAdvisor ? 'Complimentary membership' : 'Monthly billing · No contract · Cancel anytime'}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', background: 'rgba(0,77,53,0.04)', border: '1.5px solid rgba(0,77,53,0.15)', borderRadius: 12, marginBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 16, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif' }}>Viysr Advisor Membership</span>
                {isCompedAdvisor
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', background: '#fef3c7', padding: '3px 10px', borderRadius: 999, border: '1px solid #fde68a' }}>{compExpired ? 'Comp Expired' : 'Comped'}</span>
                  : <StatusBadge status={billingStatus} />
                }
              </div>
              <div style={{ fontSize: 13, color: '#888', fontFamily: 'sans-serif' }}>
                {isCompedAdvisor
                  ? (compActive ? `$0/month · Complimentary until ${compedUntilLabel}` : compExpired ? 'Comp period has ended — payment method required' : '$0/month · Complimentary')
                  : isBeta ? '$0/month (beta)' : `$${monthlyPrice}/month`
                }
                {!isCompedAdvisor && billingStatus === 'active' && nextBillingDate && ` · Next billing ${nextBillingDate}`}
                {!isCompedAdvisor && billingStatus === 'cancelled' && periodEnd && ` · Active until ${periodEnd}`}
              </div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: isCompedAdvisor ? '#92400e' : G, fontFamily: 'Georgia, serif' }}>
              {isCompedAdvisor ? '$0' : isBeta ? '$0' : `$${monthlyPrice}`}
              <span style={{ fontSize: 13, color: '#aaa', fontWeight: 400 }}>/mo</span>
            </div>
          </div>

          {/* Only show cancel/reactivate for real Stripe subscribers */}
          {!isCompedAdvisor && billingStatus === 'active' && hasStripeCustomer && (
            <button onClick={() => setShowCancelModal(true)} style={{ background: '#fff', color: '#888', border: `1.5px solid ${BORDER}`, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontFamily: 'sans-serif', fontWeight: 600, cursor: 'pointer' }}>
              Cancel subscription
            </button>
          )}
          {!isCompedAdvisor && billingStatus === 'cancelled' && (
            <button onClick={handleReactivate} disabled={reactivating} style={{ background: G, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontFamily: 'sans-serif', fontWeight: 700, cursor: 'pointer' }}>
              {reactivating ? 'Reactivating…' : 'Reactivate subscription'}
            </button>
          )}
        </SectionCard>

        {/* ── PAYMENT METHOD ── */}
        <SectionCard title="Payment Method" subtitle="Managed securely through Stripe.">
          {compExpired && !hasStripeCustomer ? (
            <>
              <div style={{ background: RED_BG, border: `1.5px solid ${RED_BORDER}`, borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: RED, fontFamily: 'sans-serif', marginBottom: 2 }}>Your complimentary membership has ended</div>
                <div style={{ fontSize: 13, color: '#666', fontFamily: 'sans-serif', lineHeight: 1.5 }}>Add your payment method to keep your profile active and continue appearing in search results.</div>
              </div>
              {stripePromise && (
                <Elements stripe={stripePromise}>
                  <SetupStripeForm
                    getAuthHeader={getAuthHeader}
                    onSuccess={() => { showToast('Payment method added — your subscription is now active.'); loadData(); }}
                    onCancel={null}
                  />
                </Elements>
              )}
            </>
          ) : compActive && !hasStripeCustomer ? (
            <>
              <div style={{ background: AMBER_BG, border: `1.5px solid ${AMBER_BORDER}`, borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: AMBER, fontFamily: 'sans-serif', marginBottom: 2 }}>Complimentary until {compedUntilLabel} — no card required yet</div>
                <div style={{ fontSize: 13, color: '#666', fontFamily: 'sans-serif', lineHeight: 1.5 }}>Add a payment method now and your subscription will start automatically when your comp period ends.</div>
              </div>
              {!showUpdatePayment && (
                <button onClick={() => setShowUpdatePayment(true)} style={{ background: G, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif' }}>
                  Add payment method →
                </button>
              )}
              {showUpdatePayment && stripePromise && (
                <Elements stripe={stripePromise}>
                  <SetupStripeForm
                    getAuthHeader={getAuthHeader}
                    onSuccess={() => { setShowUpdatePayment(false); showToast('Payment method added — your subscription will activate when your comp period ends.'); loadData(); }}
                    onCancel={() => setShowUpdatePayment(false)}
                  />
                </Elements>
              )}
            </>
          ) : isBetaAdvisor ? (
            <>
              <div style={{ background: AMBER_BG, border: `1.5px solid ${AMBER_BORDER}`, borderRadius: 10, padding: '12px 16px', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: AMBER, fontFamily: 'sans-serif', marginBottom: 2 }}>Beta access — no card required yet</div>
                <div style={{ fontSize: 13, color: '#666', fontFamily: 'sans-serif', lineHeight: 1.5 }}>Add your billing information now to keep your profile active when the beta period ends.</div>
              </div>
              {!showUpdatePayment && (
                <button onClick={() => setShowUpdatePayment(true)} style={{ background: G, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif' }}>
                  Add billing info →
                </button>
              )}
              {showUpdatePayment && stripePromise && (
                <Elements stripe={stripePromise}>
                  <SetupStripeForm
                    getAuthHeader={getAuthHeader}
                    onSuccess={() => { setShowUpdatePayment(false); showToast('Billing set up successfully! Your subscription is now active.'); loadData(); }}
                    onCancel={() => setShowUpdatePayment(false)}
                  />
                </Elements>
              )}
            </>
          ) : paymentMethod ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ background: BG, border: `1px solid ${BORDER}`, borderRadius: 6, padding: '5px 10px', fontSize: 12, fontFamily: 'sans-serif', fontWeight: 700, color: '#444', textTransform: 'uppercase' }}>
                  {paymentMethod.brand}
                </div>
                <span style={{ fontSize: 14, fontFamily: 'sans-serif', color: '#555' }}>
                  •••• •••• •••• {paymentMethod.last4}
                </span>
                <span style={{ fontSize: 12, color: '#bbb', fontFamily: 'sans-serif' }}>
                  {paymentMethod.exp_month}/{paymentMethod.exp_year}
                </span>
              </div>
              <button
                onClick={handleOpenPortal}
                disabled={portalLoading}
                style={{ fontSize: 13, fontFamily: 'sans-serif', color: G, background: '#fff', border: `1.5px solid ${G}`, borderRadius: 999, padding: '8px 18px', cursor: portalLoading ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: portalLoading ? 0.7 : 1 }}
              >
                {portalLoading ? 'Opening…' : 'Manage billing →'}
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 14, color: '#aaa', fontFamily: 'sans-serif', marginBottom: showUpdatePayment ? 0 : undefined }}>
              No payment method on file.
            </div>
          )}

          {!isBetaAdvisor && showUpdatePayment && stripePromise && (
            <Elements stripe={stripePromise}>
              <UpdatePaymentForm
                getAuthHeader={getAuthHeader}
                onSuccess={handlePaymentUpdated}
                onCancel={() => setShowUpdatePayment(false)}
              />
            </Elements>
          )}
        </SectionCard>

        {/* ── BILLING HISTORY ── */}
        <SectionCard title="Billing History">
          {details?.invoices?.length ? (
            details.invoices.map((inv, i, arr) => (
              <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: i < arr.length - 1 ? `1px solid ${BORDER}` : 'none', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, fontFamily: 'sans-serif', color: '#555' }}>{inv.date}</span>
                <span style={{ fontSize: 14, fontFamily: 'sans-serif', color: DARK, fontWeight: 600 }}>{inv.amount}</span>
                <StatusBadge status={inv.status === 'paid' ? 'active' : inv.status} />
                {inv.pdf && (
                  <a href={inv.pdf} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: G, fontFamily: 'sans-serif', textDecoration: 'none', fontWeight: 600 }}>
                    PDF ↗
                  </a>
                )}
              </div>
            ))
          ) : (
            <div style={{ fontSize: 14, color: '#aaa', fontFamily: 'sans-serif' }}>No invoices yet.</div>
          )}
        </SectionCard>

        {/* ── REFERRAL ── */}
        <SectionCard title="Referral Code" subtitle="Share your code — you both get one free month when they activate.">
          {details?.referral_code ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div style={{ background: BG, border: `1.5px solid ${BORDER}`, borderRadius: 10, padding: '12px 20px', fontSize: 18, fontWeight: 700, color: DARK, fontFamily: 'Georgia, serif', letterSpacing: '0.05em', flex: 1 }}>
                  {details.referral_code}
                </div>
                <button onClick={handleCopyReferral} style={{ background: copied ? MINT : '#fff', border: `1.5px solid ${copied ? G : BORDER}`, color: copied ? G : '#555', padding: '12px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'sans-serif', whiteSpace: 'nowrap', transition: 'all 0.2s' }}>
                  {copied ? '✓ Copied' : 'Copy code'}
                </button>
              </div>
              <div style={{ display: 'flex', gap: 24, fontSize: 13, fontFamily: 'sans-serif', color: '#888' }}>
                <span>Referrals made: <strong style={{ color: DARK }}>{details.referralStats?.count || 0}</strong></span>
                <span>Credits earned: <strong style={{ color: DARK }}>${details.referralStats?.credits || 0}</strong></span>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 14, color: '#aaa', fontFamily: 'sans-serif' }}>Your referral code will appear here after your first billing cycle.</div>
          )}
        </SectionCard>

        {/* ── COMMUNICATION PREFERENCES ── */}
        <SectionCard title="Communication Preferences">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: DARK, fontFamily: 'sans-serif', marginBottom: 2 }}>Email updates</div>
                <div style={{ fontSize: 13, color: '#aaa', fontFamily: 'sans-serif' }}>New features, platform news, and advisor tips</div>
              </div>
              <Toggle on={!!details?.marketing_email_opt_in} onChange={v => handlePrefChange('marketing_email_opt_in', v)} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: DARK, fontFamily: 'sans-serif', marginBottom: 2 }}>SMS updates</div>
                <div style={{ fontSize: 13, color: '#aaa', fontFamily: 'sans-serif' }}>Text notifications for leads and important updates</div>
              </div>
              <Toggle on={!!details?.marketing_sms_opt_in} onChange={v => handlePrefChange('marketing_sms_opt_in', v)} />
            </div>
          </div>
        </SectionCard>

      </div>
    </div>
  );
};

export default AdvisorBillingSettings;
